import { ComponentProps } from 'react';
import Select from 'react-select';
import { useField } from 'formik';
import styled, { css, useTheme } from 'styled-components';
import { ErrorMessage } from 'styles/Styles.styled';
import { Theme } from 'styles/theme';
import { Option } from 'types/types';
import Tooltip from 'components/controls/Tooltip';
import { getCustomWizardSelectStyles } from '../epd-compiler-inputs/select-styles';

type Props = {
  options: Option[];
  name: string;
  handlePostChangeAction?(event: {
    target: {
      name: string;
      value: any;
    };
  }): void;
  placeholder?: string;
  /** Decides whether the error message renders below the field; defaults to showing any error. */
  shouldShowBottomError?: (error: string) => boolean;
  /**
   * Enables InputFormField-style error display (positioning, border highlight, custom message,
   * tooltip). Only activates when one of these four props is explicitly passed, so existing
   * usages that pass none of them keep their current appearance unchanged.
   */
  errorPositioning?: 'static' | 'absolute' | 'none';
  shouldHighlightBorder?: (error: string) => boolean;
  renderErrorMessage?: (error: string) => React.ReactNode;
  getErrorTooltip?: (error: string) => string;
} & ComponentProps<typeof Select>;

const SelectFormField: React.FC<Props> = ({
  options,
  handlePostChangeAction,
  styles,
  placeholder,
  shouldShowBottomError,
  errorPositioning,
  shouldHighlightBorder,
  renderErrorMessage,
  getErrorTooltip,
  ...props
}) => {
  const theme = useTheme() as Theme;
  const [field, meta, helpers] = useField(props.name);
  const customSelectStyles = getCustomWizardSelectStyles(theme);
  const useAdvancedErrorDisplay =
    errorPositioning !== undefined ||
    shouldHighlightBorder !== undefined ||
    renderErrorMessage !== undefined ||
    getErrorTooltip !== undefined;
  const rawError = meta.touched ? (meta.error as string | undefined) : undefined;
  const showBottomError = !!rawError && (!shouldShowBottomError || shouldShowBottomError(rawError));
  const highlightBorder =
    useAdvancedErrorDisplay && !!rawError && (shouldHighlightBorder ? shouldHighlightBorder(rawError) : true);
  const errorHighlightStyles = highlightBorder
    ? {
        control: (base: any, state: any) => ({
          ...customSelectStyles.control(base, state),
          borderColor: theme.colors.systemStatusError,
          '&:hover': { borderColor: theme.colors.systemStatusError },
        }),
      }
    : {};
  const resolveErrorTooltip = (): string | undefined => {
    if (!useAdvancedErrorDisplay || !rawError) {
      return undefined;
    }
    if (getErrorTooltip) {
      return getErrorTooltip(rawError);
    }
    return renderErrorMessage ? undefined : rawError;
  };
  const errorTooltip = resolveErrorTooltip();

  // Extract the necessary values from formik's field props
  const { name, value, onBlur, onChange } = field;

  // Create an object that matches the expected format of react-select
  const selectValue = props.isMulti
    ? options?.filter((option: Option) => value?.includes(option.value))
    : options?.find((option: Option) => option?.value === value) ?? '';

  // Define a custom onChange function that updates formik's field value
  const handleChange = (selectedOption: Option) => {
    const selectedValue = selectedOption ? selectedOption.value : '';
    if (field.value === selectedValue) {
      return;
    }
    onChangeSelectValue(selectedValue);
  };

  const handleMultiChange = (selectedOptions: Option[]) => {
    const selectedValue = selectedOptions ? selectedOptions.map((i: Option) => i.value) : [];
    onChangeSelectValue(selectedValue);
  };

  const onChangeSelectValue = (value: any) => {
    const event = { target: { name, value } };
    onChange(event);
    if (handlePostChangeAction) {
      handlePostChangeAction(event);
    }
  };

  return (
    <SelectWrapper>
      <Select
        id={name}
        options={options}
        value={selectValue}
        onBlur={(event) => {
          helpers.setTouched(true);
          onBlur(event);
        }}
        onChange={(props.isMulti ? handleMultiChange : handleChange) as any}
        styles={{ ...customSelectStyles, ...errorHighlightStyles, ...styles }}
        placeholder={placeholder ?? 'Select'}
        {...props}
      />
      {showBottomError &&
        (useAdvancedErrorDisplay ? (
          <FieldError $errorPositioning={errorPositioning ?? 'static'}>
            {renderErrorMessage ? renderErrorMessage(rawError as string) : rawError}
            {errorTooltip && <Tooltip position="titleEllipsed">{errorTooltip}</Tooltip>}
          </FieldError>
        ) : (
          <ErrorMessage style={{ margin: '0.5rem 0 0.5rem' }}>{rawError}</ErrorMessage>
        ))}
    </SelectWrapper>
  );
};

const SelectWrapper = styled.div`
  position: relative;
`;

const FieldError = styled.div<{ $errorPositioning: 'absolute' | 'static' | 'none' }>`
  margin-top: 4px;
  margin-bottom: 0.5rem;
  font-family: 'Inter';
  font-size: 12px;
  color: ${(props) => props.theme.colors.systemStatusError};

  ${(props) =>
    props.$errorPositioning === 'absolute' &&
    css`
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      margin: 4px 0 0;
      max-height: 24px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    `}

  ${(props) =>
    props.$errorPositioning === 'none' &&
    css`
      display: none;
    `}
`;

export default SelectFormField;
